import {
  faExternalLinkAlt,
  faFileInvoice,
  faReply,
  faRedo
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import corner1 from 'assets/img/illustrations/corner-1.png';
import FalconComponentCard from 'components/common/FalconComponentCard';
import Flex from 'components/common/Flex';
import Pagination from 'components/common/Pagination';
import SoftBadge from 'components/common/SoftBadge';
import AdvanceTable from 'components/common/advance-table/AdvanceTable';
import AdvanceTableFooter from 'components/common/advance-table/AdvanceTableFooter';
import AdvanceTableSearchBox from 'components/common/advance-table/AdvanceTableSearchBox';
import AdvanceTableWrapper from 'components/common/advance-table/AdvanceTableWrapper';
import segmentHelper from 'helpers/segment-helper';
import _ from 'lodash';
import moment from 'moment';
import React, { useEffect, useState } from 'react';
import {
  Button,
  Col,
  Form,
  ListGroup,
  Modal,
  Row,
  Spinner
} from 'react-bootstrap';
import { useDispatch } from 'react-redux';
import { useSelector } from 'react-redux';
import { useAsyncDebounce } from 'react-table';
import { useListPaymentHistoryQuery } from 'rtk/payment.slice';
import {
  paymentMethodGet,
  retrySubscriptionPayment
} from 'stores/subscription';
import api from 'util/api';
import enums from 'util/enums';
import filters from 'util/filters';

const PaymentHistoryList = () => {
  const [pageSize, setPageSize] = useState(10);
  const [pageIndex, setPageIndex] = useState(0);
  const [searchKeyword, setSearchKeyword] = useState('');
  const [retryingPaymentId, setRetryingPaymentId] = useState(null);
  const [showCardModal, setShowCardModal] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [selectedCardForRetry, setSelectedCardForRetry] = useState(null);
  const [selectedCardDetails, setSelectedCardDetails] = useState(null);
  const [paymentToRetry, setPaymentToRetry] = useState(null);
  const [retryStatus, setRetryStatus] = useState(null);
  const [retryErrorMessage, setRetryErrorMessage] = useState(null);
  const { subscriptionStatus, paymentCards } = useSelector(
    state => state.subscription
  );
  let activeSubscriptions = _.get(subscriptionStatus, 'Active', []);
  const dispatch = useDispatch();
  const { data, refetch, isLoading, isFetching, isError } =
    useListPaymentHistoryQuery({
      pageSize,
      skip: pageIndex * pageSize,
      searchValue: searchKeyword
    });
  const paymentHistoryList = data?.Objects || [];
  const totalRecords = data?.TotalRecords || 0;

  const loading = isLoading || isFetching;
  const fetchingHistory = isLoading;
  const endOfResults = !!data?.EndOfResults;

  const unapprovedPayments = (paymentHistoryList || []).filter(
    p => !p.Approved
  );
  const lastFailedCharge =
    _.orderBy(unapprovedPayments, ['Created'], ['desc'])[0] || null;
  const hasUnapprovedPayments = unapprovedPayments.length > 0;

  const openRetryCardModal = () => {
    if (retryingPaymentId) return;
    dispatch(paymentMethodGet());
    setSelectedCardForRetry(null);
    setPaymentToRetry(lastFailedCharge);
    setShowCardModal(true);
  };

  const closeCardModal = () => {
    setShowCardModal(false);
    setSelectedCardForRetry(null);
    setPaymentToRetry(null);
  };

  const onCardSelectedContinue = () => {
    if (!selectedCardForRetry) return;
    setRetryStatus(null);
    setRetryErrorMessage(null);
    const card = (paymentCards || []).find(
      c => c.GUID === selectedCardForRetry
    );
    setSelectedCardDetails(
      card
        ? {
            CardLast4: card.CardLast4,
            ExpMonth: card.ExpMonth,
            ExpYear: card.ExpYear,
            IsDefaultPaymentMethod: card.IsDefaultPaymentMethod,
            IsBackupPaymentMethod: card.IsBackupPaymentMethod
          }
        : null
    );
    setShowCardModal(false);
    setShowConfirmModal(true);
  };

  const confirmRetryPayment = () => {
    if (!paymentToRetry || !selectedCardForRetry) return;

    setRetryStatus('loading');
    setRetryErrorMessage(null);
    setRetryingPaymentId(paymentToRetry.GUID);

    dispatch(
      retrySubscriptionPayment(
        paymentToRetry.SubscriptionGUID,
        selectedCardForRetry
      )
    )
      .then(() => {
        setRetryingPaymentId(null);
        setRetryStatus('success');
        refetch();
      })
      .catch(err => {
        setRetryingPaymentId(null);
        setRetryStatus('error');
        setRetryErrorMessage(
          err?.Message || err?.message || 'Failed to retry payment'
        );
      });
  };

  const closeConfirmModal = () => {
    setShowConfirmModal(false);
    setSelectedCardForRetry(null);
    setSelectedCardDetails(null);
    setPaymentToRetry(null);
    setRetryStatus(null);
    setRetryErrorMessage(null);
  };

  useEffect(() => {
    refetch();
  }, []);

  const columns = [
    {
      accessor: 'StripeChargeId',
      Header: 'Charge ID',
      Cell: rowData => {
        return (
          <>
            {rowData.row.original?.StripeChargeId + ' '}
            {activeSubscriptions &&
            activeSubscriptions.length > 0 &&
            activeSubscriptions[0]?.GUID ===
              rowData.row.original?.SubscriptionGUID ? (
              <SoftBadge pill bg="success" className="me-2" size="sm">
                Active
              </SoftBadge>
            ) : null}
          </>
        );
      }
    },
    {
      accessor: 'ChargeAmount',
      Header: 'Amount',
      Cell: rowData => {
        const { ChargeAmount, RefundAmount } = rowData.row.original;
        return (
          <>
            {filters.formatCurrencyFromServer(ChargeAmount.toFixed(2), 'USD')}{' '}
            {RefundAmount ? (
              <SoftBadge pill bg="secondary" className="me-2">
                {filters.formatCurrencyFromServer(
                  RefundAmount.toFixed(2),
                  'USD'
                )}{' '}
                Refunded
              </SoftBadge>
            ) : (
              <></>
            )}
          </>
        );
      }
    },
    {
      accessor: 'DiscountPercentage',
      Header: 'Discount',
      Cell: rowData => {
        const { DiscountPercentage } = rowData.row.original;
        return <>{DiscountPercentage || 0}%</>;
      }
    },
    {
      accessor: 'Approved',
      Header: 'Approved',
      Cell: rowData => {
        const { Approved } = rowData.row.original;
        return Approved ? (
          <SoftBadge pill bg="success" className="me-2">
            {'Approved'}
          </SoftBadge>
        ) : (
          <SoftBadge pill bg="warning" className="me-2">
            {'Declined'}
          </SoftBadge>
        );
      }
    },
    {
      accessor: 'Created',
      Header: 'Created',
      Cell: rowData => {
        const { Created } = rowData.row.original;
        return moment(Created).local().format('MMMM Do YYYY, h:mm a');
      }
    },
    {
      accessor: 'AttemptedCardType',
      Header: 'Attempted Card',
      Cell: rowData => {
        return rowData.row.original?.AttemptedCardType ? (
          rowData.row.original?.AttemptedCardType === 'primary' ? (
            <SoftBadge pill bg="success" className="me-2" size="sm">
              Primary
            </SoftBadge>
          ) : rowData.row.original?.AttemptedCardType === 'backup' ? (
            <SoftBadge pill bg="warning" className="me-2" size="sm">
              Backup
            </SoftBadge>
          ) : null
        ) : (
          <></>
        );
      }
    },
    {
      accessor: 'SuccessfulCardType',
      Header: 'Successful Card',
      Cell: rowData => {
        return rowData.row.original?.SuccessfulCardType ? (
          rowData.row.original?.SuccessfulCardType === 'primary' ? (
            <SoftBadge pill bg="success" className="me-2" size="sm">
              Primary
            </SoftBadge>
          ) : rowData.row.original?.SuccessfulCardType === 'backup' ? (
            <SoftBadge pill bg="warning" className="me-2" size="sm">
              Backup
            </SoftBadge>
          ) : null
        ) : (
          <></>
        );
      }
    },
    {
      accessor: 'ReceiptUrl',
      Header: 'Receipt',
      Cell: rowData => {
        const { ReceiptUrl } = rowData.row.original;
        return (
          <a
            target="_blank"
            rel="noreferrer"
            href={ReceiptUrl}
            onClick={() =>
              segmentHelper.track(segmentHelper.events.VIEW_RECEIPT)
            }
          >
            <FontAwesomeIcon icon={faExternalLinkAlt} size="sm" /> &nbsp; View
          </a>
        );
      }
    }
  ];

  const filteredPaymentCards = _.sortBy(
    (paymentCards || []).filter(card => {
      if (!card?.ExpYear || !card?.ExpMonth) return true;
      const now = new Date();
      const cardYear = parseInt(card.ExpYear);
      const cardMonth = parseInt(card.ExpMonth);
      return !(
        cardYear < now.getFullYear() ||
        (cardYear === now.getFullYear() && cardMonth < now.getMonth() + 1)
      );
    }),
    card => {
      if (card?.IsDefaultPaymentMethod) return 0;
      if (card?.IsBackupPaymentMethod) return 1;
      return 2;
    }
  );

  const searchItems = useAsyncDebounce(e => {
    const keyword = e.target.value.toLowerCase().trim();
    setSearchKeyword(keyword);
    setPageIndex(0);
  }, 400);

  return (
    <>
      <Row>
        <Col sm={12}>
          <Flex
            className="w-100"
            justifyContent="center"
            alignContent="center"
            alignItems="center"
          >
            {fetchingHistory && (
              <div className="w-100 text-center">
                <Spinner /> <br />
                <br />
                <p>Loading Payment History</p>
              </div>
            )}
          </Flex>

          {!loading && isError && (
            <div className="w-100 mt-3 text-center">
              <p className="text-center">
                {'Unable to load payment history. Try again'}
              </p>
              <Button onClick={() => refetch()} variant="outline-primary">
                <FontAwesomeIcon icon={faReply} size="sm" /> Reload Payment
                History &nbsp;{' '}
              </Button>
            </div>
          )}

          {paymentHistoryList?.length > 0 && (
            <FalconComponentCard>
              <FalconComponentCard.Header
                title="Payment history"
                subtitle="View your transaction history."
                image={corner1}
                endEl={
                  <div>
                    <Form.Control
                      size="sm"
                      onChange={e => searchItems(e)}
                      placeholder="Search transaction..."
                      name="search"
                    ></Form.Control>
                  </div>
                }
              />
              <FalconComponentCard.Body className="pt-0">
                {paymentHistoryList.length > 0 && !isLoading && (
                  <Pagination
                    pageIndex={pageIndex}
                    pageSize={pageSize}
                    totalRecords={totalRecords}
                    currentCount={paymentHistoryList.length}
                    onPageChange={setPageIndex}
                    onPageSizeChange={setPageSize}
                    endOfResults={endOfResults}
                    disabled={loading}
                    onRefresh={refetch}
                    loading={loading}
                  />
                )}
                <div className={loading ? 'refreshing-skeleton' : undefined}>
                  <AdvanceTableWrapper
                    columns={columns}
                    data={paymentHistoryList}
                    sortable
                    sortBy={[{ id: 'Created', desc: true }]}
                    pagination
                    perPage={pageSize}
                  >
                    <Row className="flex-end-center mb-3">
                      {hasUnapprovedPayments && (
                        <Col xs="auto">
                          <Button
                            variant="outline-primary"
                            size="sm"
                            onClick={openRetryCardModal}
                            disabled={!!retryingPaymentId}
                          >
                            {retryingPaymentId ? (
                              <>
                                <Spinner size="sm" />
                                Retrying...
                              </>
                            ) : (
                              <>
                                <FontAwesomeIcon icon={faRedo} size="sm" />{' '}
                                &nbsp; Retry payment
                              </>
                            )}
                          </Button>
                        </Col>
                      )}
                    </Row>
                    <AdvanceTable
                      table
                      headerClassName="bg-200 text-900 text-nowrap align-middle"
                      rowClassName="align-middle white-space-nowrap"
                      tableProps={{
                        bordered: true,
                        striped: true,
                        className: 'fs--1 mb-0 overflow-hidden'
                      }}
                    />
                  </AdvanceTableWrapper>
                </div>
              </FalconComponentCard.Body>
            </FalconComponentCard>
          )}

          {!fetchingHistory && paymentHistoryList?.length == 0 && (
            <div className="text-center mt-3">
              <FontAwesomeIcon icon={faFileInvoice} size="3x" />
              <br />
              <p className="my-3">You don't have transaction history</p>
            </div>
          )}
        </Col>
      </Row>

      <Modal
        show={showCardModal}
        onHide={closeCardModal}
        size="md"
        aria-labelledby="card-modal-title"
        centered
        backdrop="static"
      >
        <Modal.Header closeButton>
          <Modal.Title id="card-modal-title">Select payment card</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {paymentToRetry && (
            <p className="text-muted mb-2">
              Retrying last declined charge: {paymentToRetry.StripeChargeId} —{' '}
              {filters.formatCurrencyFromServer(
                (paymentToRetry.ChargeAmount || 0).toFixed(2),
                'USD'
              )}{' '}
              ({moment(paymentToRetry.Created).local().format('MMM Do YYYY')})
            </p>
          )}
          <Form.Label className="mt-2">Select card</Form.Label>
          <ListGroup>
            {_.map(filteredPaymentCards, card => (
              <ListGroup.Item key={card.GUID}>
                <Flex style={{ justifyContent: 'space-between' }}>
                  <div className="mr-1">
                    <Form.Check
                      className="mb-0"
                      type="radio"
                      checked={selectedCardForRetry === card.GUID}
                      onChange={() => setSelectedCardForRetry(card.GUID)}
                      id={`retry-card-${card.GUID}`}
                      label={`Card ending with .... ${card.CardLast4}`}
                      name="retry-payment-card"
                    />
                  </div>
                  <div>
                    {card?.IsDefaultPaymentMethod && (
                      <SoftBadge pill bg="success" className="me-2" size="sm">
                        Primary
                      </SoftBadge>
                    )}
                    {card?.IsBackupPaymentMethod && (
                      <SoftBadge pill bg="warning" className="me-2" size="sm">
                        Backup
                      </SoftBadge>
                    )}
                  </div>
                  <div className="ml-1">
                    {card.ExpMonth}/{card.ExpYear}
                  </div>
                </Flex>
              </ListGroup.Item>
            ))}
          </ListGroup>
          {(!paymentCards || paymentCards.length === 0) && (
            <p className="text-muted small mt-2">
              No payment cards on file. Add a card in Subscription Management.
            </p>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={closeCardModal}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={onCardSelectedContinue}
            disabled={!selectedCardForRetry}
          >
            Continue
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal
        show={showConfirmModal}
        onHide={() => retryStatus !== 'loading' && closeConfirmModal()}
        size="md"
        aria-labelledby="confirm-modal-title"
        centered
        backdrop="static"
      >
        <Modal.Header closeButton>
          <Modal.Title id="confirm-modal-title">
            Authorize payment attempt
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {retryStatus === 'loading' && (
            <div className="text-center py-3">
              <Spinner animation="border" className="mb-2" />
              <div>Attempting payment...</div>
            </div>
          )}
          {retryStatus === 'success' && (
            <div className="text-center py-3 text-success">
              <div className="fw-semibold mb-1">Payment successful</div>
              <div>Your subscription has been reactivated.</div>
            </div>
          )}
          {retryStatus === 'error' && (
            <div className="text-center py-3 text-danger">
              <div className="fw-semibold mb-1">Payment attempt failed</div>
              <div>{retryErrorMessage}</div>
            </div>
          )}
          {retryStatus !== 'loading' &&
            retryStatus !== 'success' &&
            retryStatus !== 'error' && (
              <>
                {selectedCardDetails && (
                  <div className="text-dark mb-3">
                    <div className="mb-1 fw-semibold">Selected card:</div>
                    <Flex
                      alignItems="center"
                      className="flex-wrap gap-2"
                      style={{ justifyContent: 'space-between' }}
                    >
                      <span>
                        Card ending with .... {selectedCardDetails.CardLast4}
                        {selectedCardDetails.ExpMonth &&
                          selectedCardDetails.ExpYear && (
                            <span className="text-muted ms-1">
                              ({selectedCardDetails.ExpMonth}/
                              {selectedCardDetails.ExpYear})
                            </span>
                          )}
                      </span>
                      <span>
                        {selectedCardDetails.IsDefaultPaymentMethod && (
                          <SoftBadge
                            pill
                            bg="success"
                            className="me-1"
                            size="sm"
                          >
                            Primary
                          </SoftBadge>
                        )}
                        {selectedCardDetails.IsBackupPaymentMethod && (
                          <SoftBadge pill bg="warning" size="sm">
                            Backup
                          </SoftBadge>
                        )}
                      </span>
                    </Flex>
                  </div>
                )}
                <div className="text-dark mb-0">
                  {paymentToRetry?.ChargeAmount != null && (
                    <div className="fw-semibold mb-1">
                      Amount:{' '}
                      {filters.formatCurrencyFromServer(
                        (paymentToRetry.ChargeAmount || 0).toFixed(2),
                        'USD'
                      )}
                    </div>
                  )}
                  <div>
                    This will attempt to charge the selected payment method for
                    your most recent failed payment. If successful, your
                    subscription will be reactivated.
                  </div>
                </div>
              </>
            )}
        </Modal.Body>
        <Modal.Footer>
          {retryStatus === 'loading' && (
            <Button variant="secondary" disabled>
              Attempting...
            </Button>
          )}
          {(retryStatus === 'success' || retryStatus === 'error') && (
            <Button variant="primary" onClick={closeConfirmModal}>
              Close
            </Button>
          )}
          {retryStatus !== 'loading' &&
            retryStatus !== 'success' &&
            retryStatus !== 'error' && (
              <>
                <Button variant="secondary" onClick={closeConfirmModal}>
                  Cancel
                </Button>
                <Button variant="primary" onClick={confirmRetryPayment}>
                  Authorize
                </Button>
              </>
            )}
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default PaymentHistoryList;
